import { useEffect, useMemo, useState } from 'react';
import { Eye, EyeOff, Filter, RefreshCcw } from 'lucide-react';
import * as reactiveUtils from '@arcgis/core/core/reactiveUtils.js';
import { refreshLayerMetadata, useLayerRegistry } from '@/map/layers/registry';
import { applyLayerStyling } from '@/map/layers/factory';
import {
  getLayerColor,
  hexToRgb,
  rgbToHex,
  setLayerColor,
} from '@/map/layers/symbology';
import { isColorableGeometry } from '@/map/layers/renderer';
import { ensureLayerOnMap, getMap, setLayerVisible, useMapStore } from '@/map/mapStore';
import { cn } from '@/lib/cn';
import type { LayerRecord, LayerStatus } from '@/map/layers/types';

export function LayersPanel() {
  const records = useLayerRegistry((s) => s.records);
  const hydrated = useLayerRegistry((s) => s.hydrated);

  if (!hydrated) {
    return <div className="p-3 text-xs text-muted-foreground">Loading layers…</div>;
  }

  return (
    <div className="flex flex-col gap-1.5 p-3">
      {records.map((r) => (
        <LayerRow key={r.id} record={r} records={records} />
      ))}
    </div>
  );
}

function LayerRow({
  record,
  records,
}: {
  record: LayerRecord;
  records: LayerRecord[];
}) {
  const mapStatus = useMapStore((s) => s.status);
  const [visible, setVisible] = useState<boolean>(false);
  const [refreshing, setRefreshing] = useState(false);
  const colorable = useMemo(
    () => isColorableGeometry(record.probe?.service.geometry_type),
    [record.probe?.service.geometry_type],
  );

  useEffect(() => {
    const m = getMap();
    if (!m) return;
    const layer = m.layers.find((l) => l.id === record.id);
    if (!layer) {
      setVisible(false);
      return;
    }
    setVisible(layer.visible);
    const handle = reactiveUtils.watch(
      () => layer.visible,
      (v) => setVisible(v),
    );
    return () => handle.remove();
  }, [record.id, mapStatus]);

  const color = getLayerColor(record.id, records);
  const hex = rgbToHex(color);

  const onToggleVisibility = async () => {
    const next = !visible;
    setVisible(next);
    if (next) {
      await ensureLayerOnMap(record.id);
    }
    setLayerVisible(record.id, next);
  };

  const onColorChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const rgb = hexToRgb(e.target.value);
    if (!rgb) return;
    setLayerColor(record.id, rgb);
    applyLayerStyling(record, records);
  };

  const onRefresh = async () => {
    setRefreshing(true);
    try {
      await refreshLayerMetadata(record.id);
    } catch {
      // status reflected in the registry
    } finally {
      setRefreshing(false);
    }
  };

  const secondary = record.flags.has('secondary');

  return (
    <div className="flex items-center gap-2 rounded-md border border-border px-2 py-1.5">
      {colorable ? (
        <label
          className="size-4 shrink-0 cursor-pointer rounded-sm border border-border"
          style={{ backgroundColor: hex }}
          title="Pick color"
        >
          <input
            type="color"
            value={hex}
            onChange={onColorChange}
            className="sr-only"
            aria-label={`Color for ${record.id}`}
          />
        </label>
      ) : (
        <span className="size-4 shrink-0 rounded-sm border border-dashed border-border" />
      )}

      <div className="min-w-0 flex-1">
        <div className="truncate text-xs font-medium text-foreground">
          {record.id}
          {secondary && (
            <span className="ml-1.5 text-[10px] uppercase tracking-wider text-muted-foreground">
              secondary
            </span>
          )}
        </div>
        <div className="truncate text-[10px] text-muted-foreground">
          {record.probe?.service.geometry_type ?? '—'}
          {record.probe?.fields.length
            ? ` · ${record.probe.fields.length} fields`
            : ''}
        </div>
      </div>

      {record.definition_expression && (
        <span
          className="grid size-5 place-items-center rounded-full bg-amber-500/20 text-amber-400"
          title={`Filtered: ${record.definition_expression}`}
          aria-label={`Layer is filtered by ${record.definition_expression}`}
        >
          <Filter className="size-3" />
        </span>
      )}

      <StatusBadge status={record.status} error={record.error} />

      <button
        type="button"
        onClick={onRefresh}
        disabled={refreshing || record.status === 'probing'}
        className="grid size-7 place-items-center rounded text-muted-foreground hover:bg-accent hover:text-accent-foreground disabled:opacity-50"
        aria-label="Refresh metadata"
        title="Refresh metadata"
      >
        <RefreshCcw className={cn('size-3.5', refreshing && 'animate-spin')} />
      </button>

      <button
        type="button"
        onClick={onToggleVisibility}
        className={cn(
          'grid size-7 place-items-center rounded text-muted-foreground hover:bg-accent hover:text-accent-foreground',
          visible && 'text-foreground',
        )}
        aria-label={visible ? 'Hide layer' : 'Show layer'}
        title={visible ? 'Hide layer' : 'Show layer'}
      >
        {visible ? <Eye className="size-3.5" /> : <EyeOff className="size-3.5" />}
      </button>
    </div>
  );
}

function StatusBadge({ status, error }: { status: LayerStatus; error?: string }) {
  const dot =
    status === 'ready'
      ? 'bg-emerald-500'
      : status === 'probing'
        ? 'bg-yellow-400'
        : status === 'error'
          ? 'bg-red-500'
          : 'bg-zinc-400';
  return (
    <div
      className="flex items-center gap-1.5 text-[10px] uppercase tracking-wider text-muted-foreground"
      title={error}
    >
      <span className={cn('size-1.5 rounded-full', dot)} aria-hidden="true" />
      {status}
    </div>
  );
}
